import type { ActionId } from "./actions.ts";
import { codeOfPress } from "./bindings.ts";
import type { HeldKeys } from "./held.ts";
import type { KeyTable } from "./page.ts";

/**
 * Whether a key press is an action's (P1.9-T50; ADR-061's amendments "key rebinding", decision 12, and "every key
 * rebindable but Escape", point 5): the one test every reader of a rebindable key goes through. The table says which
 * action a press is, by its physical key. Tab, Space and Enter are a focused control's own, so whichever action is
 * bound to one takes a press only while the game has focus.
 */

/**
 * The elements whose focus is the game's own: the page and the canvas.
 */
const GAME_TAGS: ReadonlySet<string> = new Set(["BODY", "HTML", "CANVAS"]);

/**
 * Whether the game has focus: nothing is focused, or the page itself or the canvas is, and no control. Any canvas
 * counts as the game's, by its tag: the page has one, and a focusable canvas inside a panel would need this to ask
 * which.
 *
 * @param target What had focus when the key was pressed, or null.
 * @returns True when no control has focus.
 * @example
 * gameFocused(document.body);
 * // true
 */
export function gameFocused(target: unknown): boolean {
  if (target === null || target === undefined) {
    return true;
  }
  const tag = (target as { readonly tagName?: unknown }).tagName;
  return typeof tag === "string" && GAME_TAGS.has(tag.toUpperCase());
}

/**
 * The physical keys a focused control keeps for itself: Tab moves focus on, and Space and Enter press the control.
 */
const FOCUS_CODES: ReadonlySet<string> = new Set(["Tab", "Space", "Enter", "NumpadEnter"]);

/**
 * Whether a press of a physical key may be an action's with this focus: any key while the game has focus, and with a
 * control focused any key but Tab, Space and Enter, which the control keeps.
 *
 * @param code The press's physical key, or null.
 * @param target What had focus when the key was pressed.
 * @returns True when an action may take the press.
 */
export function focusAllows(code: string | null, target: unknown): boolean {
  return code === null || !FOCUS_CODES.has(code) || gameFocused(target);
}

/**
 * Whether a key going down may be held by a held action (Move, Sprint, Show item names) under the focus rule
 * (P1.9-T174, point 5): the game owns the keyboard and, for Tab, Space and Enter, the game itself has focus, so a
 * focused button keeps its own Space and Enter. A release is never held to it.
 *
 * @param event The press: its key, its physical key when the event says, and what had focus.
 * @param owned Whether the game owned the keyboard then.
 * @returns True when the key may be held.
 * @example
 * heldOwned({ key: " ", code: "Space", target: button }, true);
 * // false
 */
export function heldOwned(event: { readonly key: string; readonly code?: string | undefined; readonly target?: unknown }, owned: boolean): boolean {
  return owned && focusAllows(codeOfPress(event), event.target);
}

/**
 * Hands a key going down or up to Move's held keys (P1.9-T174): a press is held only under the focus rule
 * (`heldOwned`) and when no focused control or open layer takes it (`taken`, the router's `takenElsewhere`), so the
 * arrows a surface uses never walk; a release always lets go, whoever takes it.
 *
 * @param held Move's held keys.
 * @param event The key: its key, its physical key when the event says, and what had focus.
 * @param down Whether it went down.
 * @param owned Whether the game owned the keyboard then.
 * @param taken Whether a focused control or the top layer takes the press.
 * @example
 * holdMoveKey(held, event, true, owned, (press) => input.takenElsewhere(press));
 */
export function holdMoveKey<E extends { readonly key: string; readonly code?: string | undefined; readonly target?: unknown }>(held: Pick<HeldKeys, "key">, event: E, down: boolean, owned: boolean, taken: (event: E) => boolean): void {
  held.key(event.code, down, down && heldOwned(event, owned) && !taken(event));
}

/**
 * Whether a press is an action's, by the physical key the table binds it to now, Tab, Space and Enter only while the
 * game has focus.
 *
 * @param keys The key table.
 * @param action The action.
 * @param event The press: its key, its physical key when the event says, and what had focus.
 * @returns True when the action may take the press.
 * @example
 * actionPress(pageKeys, "inventory", event);
 */
export function actionPress(keys: Pick<KeyTable, "actionOf">, action: ActionId, event: { readonly key: string; readonly code?: string | undefined; readonly target?: unknown }): boolean {
  return keys.actionOf(event) === action && focusAllows(codeOfPress(event), event.target);
}
